import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { ApiError, api } from '@/lib/api';
import {
  type AdminUser,
  type CreateUserRequest,
  CreateUserRequestSchema,
  type DataSource,
  type RbacRole,
  type Role,
  type Skill,
  type ToolVisibility,
  type UpdateUserRequest,
  UpdateUserRequestSchema,
  type UserAttributesRecord,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal } from './Modal';
import { UserAttributesEditor } from './UserAttributesEditor';

type Mode = { kind: 'create' } | { kind: 'edit'; user: AdminUser };

interface Props {
  open: boolean;
  mode: Mode | null;
  onClose: () => void;
}

const EMPTY_CREATE: CreateUserRequest = {
  email: '',
  displayName: '',
  role: 'user',
  password: '',
};

export function UserFormModal({ open, mode, onClose }: Props) {
  if (!open || !mode) return null;
  return mode.kind === 'create' ? (
    <CreateForm onClose={onClose} />
  ) : (
    <EditForm onClose={onClose} user={mode.user} />
  );
}

function ExternalIdentitiesEditor({
  role,
  value,
  onChange,
}: {
  role: Role;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'data-sources'],
    queryFn: api.dataSourceList,
  });
  const sources: DataSource[] = data?.dataSources ?? [];
  const setFor = (id: string, v: string) => {
    const next = { ...value };
    if (v.trim() === '') {
      delete next[id];
    } else {
      next[id] = v.trim();
    }
    onChange(next);
  };
  if (role === 'admin') {
    return (
      <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
        관리자는 user_scope_column 필터를 우회하므로 정체성 매핑이 필수 아닙니다.
      </div>
    );
  }
  if (isLoading) {
    return <div className="text-sm text-foreground-muted">데이터소스 목록을 불러오는 중…</div>;
  }
  if (sources.length === 0) {
    return <div className="text-sm text-foreground-muted">등록된 데이터소스가 없습니다.</div>;
  }
  return (
    <div className="space-y-2 rounded-md border border-border p-2 max-h-56 overflow-y-auto">
      {sources.map((s) => (
        <label key={s.id} className="flex items-center gap-2 text-sm">
          <span className="min-w-[140px] font-medium truncate" title={s.name}>
            {s.name}
          </span>
          <input
            type="text"
            value={value[s.id] ?? ''}
            onChange={(e) => setFor(s.id, e.target.value)}
            placeholder="예: hong@co.kr 또는 EMP-001"
            className="h-8 flex-1 rounded border border-border bg-surface px-2 text-sm"
          />
        </label>
      ))}
    </div>
  );
}

function DataSourceAdminSelector({
  role,
  value,
  onChange,
}: {
  role: Role;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'data-sources'],
    queryFn: api.dataSourceList,
  });
  const sources: DataSource[] = data?.dataSources ?? [];
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  if (role === 'admin') {
    return (
      <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
        관리자는 모든 데이터소스의 관리자로 간주됩니다.
      </div>
    );
  }
  if (isLoading) {
    return <div className="text-sm text-foreground-muted">데이터소스 목록을 불러오는 중…</div>;
  }
  if (sources.length === 0) {
    return <div className="text-sm text-foreground-muted">등록된 데이터소스가 없습니다.</div>;
  }
  return (
    <div className="space-y-1 rounded-md border border-border p-2 max-h-40 overflow-y-auto">
      {sources.map((s) => {
        const checked = value.includes(s.id);
        return (
          <label
            key={s.id}
            className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-surface-muted rounded cursor-pointer"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(s.id)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-foreground-muted line-clamp-1">
                {s.kind} · {s.host}:{s.port}/{s.database}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

export function SkillGrantSelector({
  role,
  value,
  onChange,
}: {
  role: Role;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: api.adminSkillList,
  });
  const skills: Skill[] = (data?.skills ?? []).filter((s) => s.enabled);
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  if (role === 'admin') {
    return (
      <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
        관리자는 모든 활성화된 Skill 을 자동으로 사용합니다.
      </div>
    );
  }
  if (isLoading) {
    return <div className="text-sm text-foreground-muted">Skill 목록을 불러오는 중…</div>;
  }
  if (skills.length === 0) {
    return (
      <div className="text-sm text-foreground-muted">
        활성화된 Skill 이 없습니다. 관리자 &rsaquo; Skill 페이지에서 추가하세요.
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border border-border p-2 max-h-48 overflow-y-auto">
      {skills.map((s) => {
        const checked = value.includes(s.id);
        return (
          <label
            key={s.id}
            className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-surface-muted rounded cursor-pointer"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(s.id)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="font-medium">{s.name}</div>
              {s.description ? (
                <div className="text-xs text-foreground-muted line-clamp-1">{s.description}</div>
              ) : null}
            </div>
          </label>
        );
      })}
    </div>
  );
}

function CreateForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateUserRequest>(EMPTY_CREATE);
  const [allowedSkillIds, setAllowedSkillIds] = useState<string[]>([]);
  const [dataSourceAdminIds, setDataSourceAdminIds] = useState<string[]>([]);
  const [externalIdentities, setExternalIdentities] = useState<Record<string, string>>({});
  const [apiRelayEnabled, setApiRelayEnabled] = useState(false);
  const [attributes, setAttributes] = useState<UserAttributesRecord>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateUserRequest) => api.adminCreateUser(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '사용자 생성에 실패했습니다.');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const body: CreateUserRequest = {
      ...form,
      allowedSkillIds: form.role === 'admin' ? undefined : allowedSkillIds,
      dataSourceAdminIds: form.role === 'admin' ? undefined : dataSourceAdminIds,
      externalIdentities:
        form.role === 'admin' || Object.keys(externalIdentities).length === 0
          ? undefined
          : externalIdentities,
      apiRelayEnabled,
      attributes,
    };
    const parsed = CreateUserRequestSchema.safeParse(body);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[String(issue.path[0])] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <Modal open onClose={onClose} title="사용자 추가" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <Field label="아이디 / 이메일" htmlFor="new-user-email" error={errors.email}>
          <Input
            id="new-user-email"
            type="text"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            autoComplete="off"
            placeholder="회사 SSO/LDAP 아이디 또는 이메일 (예: E1000)"
          />
        </Field>

        <Field label="표시 이름" htmlFor="new-user-name" error={errors.displayName}>
          <Input
            id="new-user-name"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          />
        </Field>

        <Field label="역할" htmlFor="new-user-role" error={errors.role}>
          <select
            id="new-user-role"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as 'admin' | 'user' }))}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="user">사용자</option>
            <option value="admin">관리자</option>
          </select>
        </Field>

        <Field
          label="허용 Skill"
          htmlFor="new-user-allowed-skills"
          hint="이 사용자가 챗에서 쓸 수 있는 Skill. 비워두면 도구 없이 일반 대화만 가능."
        >
          <div id="new-user-allowed-skills">
            <SkillGrantSelector
              role={form.role}
              value={allowedSkillIds}
              onChange={setAllowedSkillIds}
            />
          </div>
        </Field>

        <Field
          label="데이터소스 관리자 권한"
          htmlFor="new-user-ds-admin"
          hint="이 사용자를 특정 데이터소스의 관리자로 지정. 행 필터 우회·전체 조회 가능."
        >
          <div id="new-user-ds-admin">
            <DataSourceAdminSelector
              role={form.role}
              value={dataSourceAdminIds}
              onChange={setDataSourceAdminIds}
            />
          </div>
        </Field>

        <Field
          label="외부 정체성 매핑"
          htmlFor="new-user-identities"
          hint="user_scope_column 이 설정된 테이블에서 이 사용자를 가리키는 값(이메일/사번 등). 데이터소스별로 지정."
        >
          <div id="new-user-identities">
            <ExternalIdentitiesEditor
              role={form.role}
              value={externalIdentities}
              onChange={setExternalIdentities}
            />
          </div>
        </Field>

        <Field
          label="사용자 항목"
          htmlFor="new-user-attributes"
          hint="관리자 > 사용자 항목 톱니에서 등록한 동적 항목. 정의가 없으면 표시되지 않음."
        >
          <div id="new-user-attributes">
            <UserAttributesEditor value={attributes} onChange={setAttributes} />
          </div>
        </Field>

        <Field
          label="초기 비밀번호"
          htmlFor="new-user-password"
          hint="8자 이상, 영문과 숫자 모두 포함"
          error={errors.password}
        >
          <PasswordInput
            id="new-user-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            autoComplete="new-password"
          />
        </Field>

        <Field
          label="LLM API 릴레이"
          htmlFor="new-user-api-relay"
          hint="이 사용자가 OpenAI 호환 API 키를 발급받아 외부에서 LLM 을 호출할 수 있는지. 글로벌 토글이 OFF 면 무효."
        >
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              id="new-user-api-relay"
              type="checkbox"
              checked={apiRelayEnabled}
              onChange={(e) => setApiRelayEnabled(e.target.checked)}
              className="size-4"
            />
            <span>{apiRelayEnabled ? '허용' : '비활성'}</span>
          </label>
        </Field>

        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? '생성 중…' : '사용자 만들기'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function RoleExclusionsEditor({ userId, roles }: { userId: string; roles: RbacRole[] }) {
  const qc = useQueryClient();
  const exclusionsQuery = useQuery({
    queryKey: ['admin', 'users', userId, 'role-exclusions'],
    queryFn: () => api.adminUserGetRoleExclusions(userId),
  });
  const [pickRoleId, setPickRoleId] = useState('');
  const [reason, setReason] = useState('');

  const addMutation = useMutation({
    mutationFn: () =>
      api.adminUserAddRoleExclusion(userId, {
        roleId: pickRoleId,
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'role-exclusions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'effective-skills'] });
      setPickRoleId('');
      setReason('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (roleId: string) => api.adminUserRemoveRoleExclusion(userId, roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'role-exclusions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'effective-skills'] });
    },
  });

  if (exclusionsQuery.isLoading) {
    return <div className="text-sm text-foreground-muted">불러오는 중…</div>;
  }
  const exclusions = exclusionsQuery.data?.exclusions ?? [];
  const excludedRoleIds = new Set(exclusions.map((e) => e.roleId));
  const candidateRoles = roles.filter((r) => !excludedRoleIds.has(r.id));

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      {exclusions.length === 0 ? (
        <div className="text-sm text-foreground-muted">제외된 그룹이 없습니다.</div>
      ) : (
        <ul className="space-y-1.5">
          {exclusions.map((ex) => (
            <li
              key={ex.roleId}
              className="flex items-start justify-between gap-2 rounded bg-surface-muted px-2 py-1.5 text-sm"
            >
              <div className="min-w-0">
                <div className="font-medium">{ex.roleName}</div>
                {ex.reason ? (
                  <div className="text-xs text-foreground-muted">사유: {ex.reason}</div>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeMutation.mutate(ex.roleId)}
                disabled={removeMutation.isPending}
              >
                복구
              </Button>
            </li>
          ))}
        </ul>
      )}

      {candidateRoles.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 border-t border-border pt-2 md:grid-cols-[1fr_2fr_auto] md:items-end">
          <div>
            <div className="mb-1 text-xs text-foreground-muted">제외할 그룹</div>
            <select
              value={pickRoleId}
              onChange={(e) => setPickRoleId(e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
            >
              <option value="">선택…</option>
              {candidateRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs text-foreground-muted">사유 (선택)</div>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 정직 중"
            />
          </div>
          <Button
            type="button"
            onClick={() => addMutation.mutate()}
            disabled={!pickRoleId || addMutation.isPending}
          >
            제외 추가
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function EffectiveSkillPreview({ userId }: { userId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', userId, 'effective-skills'],
    queryFn: () => api.adminUserEffectiveSkills(userId),
  });
  if (isLoading)
    return (
      <div className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground-muted">
        Effective Skill 불러오는 중…
      </div>
    );
  const entries = data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
        이 사용자에게 노출되는 Skill 이 없습니다. 그룹 또는 직접 부여를 추가하세요.
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-surface p-2">
      <ul className="space-y-1.5 max-h-56 overflow-y-auto">
        {entries.map((e) => (
          <li key={e.skillId} className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="font-medium">{e.skillName}</span>
            {e.sources.map((s, i) => {
              if (s.kind === 'direct') {
                return (
                  <span
                    key={`${e.skillId}-direct-${i}`}
                    className="rounded bg-warning/15 px-1.5 py-0.5 text-xs font-medium text-warning"
                    title="user_skill_grants 직접 부여"
                  >
                    직접
                  </span>
                );
              }
              if (s.kind === 'role') {
                return (
                  <span
                    key={`${e.skillId}-role-${s.roleId}`}
                    className="rounded bg-info/15 px-1.5 py-0.5 text-xs font-medium text-info"
                    title="수동 그룹 부여"
                  >
                    group: {s.roleName}
                  </span>
                );
              }
              if (s.kind === 'all_users') {
                return (
                  <span
                    key={`${e.skillId}-allusers-${s.roleId}`}
                    className="rounded bg-accent/15 px-1.5 py-0.5 text-xs font-medium text-accent"
                    title="전체 사용자 그룹 자동 부여 — 모든 비-admin 사용자에게 노출"
                  >
                    전체: {s.roleName}
                  </span>
                );
              }
              // attribute
              return (
                <span
                  key={`${e.skillId}-attr-${s.roleId}-${i}`}
                  className="rounded bg-success/15 px-1.5 py-0.5 text-xs font-medium text-success"
                  title={`attribute 룰 매칭 (${s.attributeKey}=${s.attributeValue}, ${s.matchMode})`}
                >
                  auto: {s.roleName}
                </span>
              );
            })}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-foreground-muted">
        저장 직전 화면 — 편집 중인 변경은 저장 후 반영됩니다.
      </p>
    </div>
  );
}

export function RoleAssignmentSelector({
  userRole,
  roles,
  value,
  onChange,
  isLoading,
}: {
  userRole: Role;
  roles: RbacRole[];
  value: string[];
  onChange: (next: string[]) => void;
  isLoading: boolean;
}) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  if (userRole === 'admin') {
    return (
      <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
        관리자는 모든 활성화된 Skill 을 자동으로 사용합니다. 그룹 부여 대상이 아닙니다.
      </div>
    );
  }
  if (isLoading) return <div className="text-sm text-foreground-muted">불러오는 중…</div>;
  if (roles.length === 0) {
    return (
      <div className="text-sm text-foreground-muted">
        등록된 그룹이 없습니다. 권한 &rsaquo; 그룹 페이지에서 추가하세요.
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border border-border p-2 max-h-48 overflow-y-auto">
      {roles.map((r) => {
        const checked = value.includes(r.id);
        return (
          <label
            key={r.id}
            className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-surface-muted rounded cursor-pointer"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(r.id)}
              className="mt-0.5"
              disabled={!r.enabled}
            />
            <div className="min-w-0">
              <div className="font-medium">
                {r.name}
                {!r.enabled ? (
                  <span className="ml-1 text-xs text-foreground-muted">(비활성)</span>
                ) : null}
              </div>
              {r.description ? (
                <div className="text-xs text-foreground-muted line-clamp-1">{r.description}</div>
              ) : null}
              <div className="text-xs text-foreground-muted">Skill {r.skillIds.length}개</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

type OverrideSelectValue = '' | ToolVisibility;

/**
 * 빠른 단일 필드 편집용 모달 — 표시 이름만 다룬다.
 * 그 외 권한·속성·데이터소스 등은 사용자 상세 페이지 (`/admin/users/:id`) 에서.
 */
function EditForm({ onClose, user }: { onClose: () => void; user: AdminUser }) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateUserRequest) => api.adminUpdateUser(user.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const body: UpdateUserRequest = { displayName };
    const parsed = UpdateUserRequestSchema.safeParse(body);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[String(issue.path[0])] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <Modal open onClose={onClose} title="표시 이름 편집" description={user.email} size="md">
      <form onSubmit={submit} className="space-y-4">
        <Field label="표시 이름" htmlFor="edit-user-name" error={errors.displayName}>
          <Input
            id="edit-user-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        <p className="text-xs text-foreground-muted">
          그룹·속성·데이터소스·API 릴레이 등 다른 항목은{' '}
          <span className="font-medium text-foreground">사용자 상세 페이지</span> 에서 편집.
        </p>

        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" disabled={mutation.isPending || displayName === user.displayName}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
